import { DailyVideo, useParticipantProperty, useVideoTrack } from '@daily-co/daily-react'
import classNames from '@ui-components-3/ui/lib/utils/classNames'
import useVisit from 'hooks/useVisit'
import { useMemo } from 'react'
import User from 'singletons/User'
import { Avatar } from 'ui-components-web'

type LocalParticipantTileProps = {
  sessionId: string
  isDominant?: boolean
  isLocal?: boolean
  compact?: boolean
}

const ParticipantTile = ({ sessionId, isDominant, isLocal, compact }: LocalParticipantTileProps) => {
  const participantId = useParticipantProperty(sessionId, 'user_name')
  const videoState = useVideoTrack(sessionId)

  const { isOff, persistentTrack } = videoState

  const visit = useVisit()

  const fullName = useMemo(() => {
    const defaultUserName = 'Participant'

    if (isLocal) {
      return User.provider.fullNameAndCredentials
    }

    if (participantId && visit?.memberId === participantId) {
      const { firstName, lastName } = visit.memberExtendedData?.privateData?.member || {}

      return [firstName, lastName].filter(Boolean).join(' ') || defaultUserName
    }

    if (visit?.aslInterpreter?.externalId === sessionId) {
      return 'American Sign Language Assistance'
    }

    if (visit?.interpreter?.externalId === sessionId || participantId?.toLowerCase() === 'language line') {
      return 'Language Line'
    }

    return defaultUserName
  }, [isLocal, participantId, sessionId, visit])

  const initials = useMemo(
    () =>
      isLocal
        ? 'Me'
        : fullName
            .split(/\s+/)
            .slice(0, 2)
            .map((i) => i[0])
            .join(''),
    [isLocal, fullName],
  )

  const accessibleLabel = `${fullName}${isLocal ? ' (Me)' : ''}`

  const aspectRatio = useMemo(() => {
    const { width, height } = persistentTrack?.getSettings?.() || {}

    if (width && height) {
      return width / height
    }

    return 16 / 9
  }, [persistentTrack])

  const hasVideo = !isOff && !!persistentTrack

  if (!sessionId) {
    return null
  }

  return (
    <div
      className={classNames(
        'flex items-center justify-center overflow-hidden bg-black',
        isDominant || compact
          ? 'h-full w-auto'
          : 'max-h-[210px] h-auto absolute top-3.5 left-3.5 z-10 border-2 border-white',
        !compact ? 'rounded-[4px] w-[150px] min-h-[80px]' : 'min-h-[100px]',
      )}
      style={{
        aspectRatio,
      }}
    >
      {hasVideo ? (
        <DailyVideo
          automirror={!!isLocal}
          sessionId={sessionId}
          type={'video'}
          className={classNames('w-full h-full -mb-0.5', compact && 'max-h-[210px]')}
          aria-label={accessibleLabel}
        />
      ) : (
        <div
          className={classNames('max-w-full flex items-center justify-center')}
          aria-label={`${accessibleLabel}. Camera is off`}
        >
          <Avatar
            className="w-12 h-12 bg-primary-800 absolute font-medium !typography-body"
            aria-hidden="true"
            title={accessibleLabel}
          >
            {initials}
          </Avatar>
        </div>
      )}
    </div>
  )
}
export default ParticipantTile
